import type { MyProduct } from "@/types/products";

/**
 * Where a product sits relative to its authored schedule.
 *
 * - `upcoming`: hasn't started — listed but not enterable.
 * - `active`:   today falls in the window, or the product has no window at all.
 * - `past`:     ended, but still inside the grace period, so it can be revisited.
 * - `expired`:  ended and past the grace period — listed but not enterable.
 */
export type ProductAvailability = "upcoming" | "active" | "past" | "expired";

/** The fields of a product this module reads; keeps the helpers testable. */
export type ScheduledProduct = Pick<
  MyProduct,
  "startDate" | "endDate" | "gracePeriodDays"
>;

/**
 * Local midnight for a published date.
 *
 * Payload writes a `dayOnly` date as UTC midnight, so `new Date(iso).getDate()`
 * reports the day before anywhere west of Greenwich — a program ending "Jun 30"
 * would read as ending Jun 29 for every US user. Parse the calendar date out of
 * the ISO string instead and rebuild it in local time, so the day the editor
 * picked is the day everyone compares against.
 */
const localDay = (iso: string | null | undefined): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? "");
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

/** Local midnight today, so every comparison is day-to-day. */
const startOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

/** `day` plus n whole days, still at local midnight (handles month/DST rollover). */
const addDays = (day: Date, days: number): Date =>
  new Date(day.getFullYear(), day.getMonth(), day.getDate() + days);

/**
 * Which bucket a product falls into. The end date is inclusive — a product
 * ending Jun 30 is current for all of Jun 30, and the grace period starts
 * counting the day after. A missing or negative grace period counts as none.
 */
export const productAvailability = (
  product: ScheduledProduct,
  now: Date = new Date(),
): ProductAvailability => {
  const today = startOfDay(now);
  const start = localDay(product.startDate);
  const end = localDay(product.endDate);

  if (start && today < start) return "upcoming";
  if (!end) return "active";
  if (today <= end) return "active";

  const grace = Math.max(product.gracePeriodDays ?? 0, 0);
  return today <= addDays(end, grace) ? "past" : "expired";
};

const DAY_FORMAT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

/**
 * The explanatory line on a card the user can't enter, or null when the card is
 * selectable and needs no explanation.
 */
export const availabilityLabel = (
  product: ScheduledProduct,
  availability: ProductAvailability,
): string | null => {
  if (availability === "upcoming") {
    const start = localDay(product.startDate);
    return start ? `Starts ${DAY_FORMAT.format(start)}` : null;
  }
  if (availability === "expired") {
    const end = localDay(product.endDate);
    return end ? `Ended ${DAY_FORMAT.format(end)}` : null;
  }
  return null;
};

/** True for the buckets whose cards are rendered disabled. */
export const isSelectable = (availability: ProductAvailability): boolean =>
  availability === "active" || availability === "past";
